<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <div v-if="visible" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">Apply to Another Template</h2>
        <button class="close-btn" @click="handleClose" type="button">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">
            <span class="required">*</span> Target HomeMetric Care Plan
          </label>
          <div class="custom-select" ref="planSelectRef">
            <div
              class="select-trigger"
              :class="{
                'has-placeholder': !selectedPlan,
                error: showValidationError && !selectedPlan,
                open: planDropdownOpen,
              }"
              @click="planDropdownOpen = !planDropdownOpen"
            >
              <span class="select-value">{{ planDisplayValue }}</span>
              <img :src="downArrowIcon" alt="arrow" class="select-arrow" />
              <img v-if="showValidationError && !selectedPlan" :src="alertIcon" alt="error" class="error-icon" />
            </div>
            <div v-show="planDropdownOpen" class="select-dropdown">
              <div
                v-for="option in planOptions"
                :key="option.value"
                class="select-option"
                :class="{ selected: option.value === selectedPlan }"
                @click="selectPlan(option.value)"
              >
                {{ option.label }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label"> <span class="required">*</span> Target Template </label>
          <div class="custom-select" ref="templateSelectRef">
            <div
              class="select-trigger"
              :class="{
                'has-placeholder': !selectedTemplateId,
                error: showValidationError && !selectedTemplateId,
                disabled: !selectedPlan,
                open: templateDropdownOpen,
              }"
              @click="!selectedPlan || (templateDropdownOpen = !templateDropdownOpen)"
            >
              <span class="select-value">{{ templateDisplayValue }}</span>
              <img :src="downArrowIcon" alt="arrow" class="select-arrow" />
              <img v-if="showValidationError && !selectedTemplateId" :src="alertIcon" alt="error" class="error-icon" />
            </div>
            <div v-show="templateDropdownOpen" class="select-dropdown">
              <div
                v-for="option in templateOptions"
                :key="option.value"
                class="select-option"
                :class="{
                  selected: option.value === selectedTemplateId,
                  disabled: option.disabled,
                }"
                @click="!option.disabled && selectTemplate(option.value)"
              >
                {{ option.label }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="handleClose" type="button">Cancel</button>
        <button class="btn-submit" @click="handleApply" type="button" :disabled="submitting">
          Apply
        </button>
      </div>
    </div>
  </div>

  <div v-if="showConfirmModal" class="modal-overlay">
    <div class="confirm-container">
      <div class="confirm-header">
        <h2 class="confirm-title">Confirm Replacement</h2>
        <button class="close-btn" @click="closeConfirm" type="button">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="confirm-body">
        <p>
          This will apply the current items to the selected template. Existing items in the target
          template will be replaced.
        </p>
        <p>Are you sure you want to continue?</p>
      </div>

      <div class="confirm-footer">
        <button class="btn-no" @click="closeConfirm" type="button">No</button>
        <button class="btn-confirm" @click="confirmApply" type="button">Confirm</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {
  getCareTemplateList,
  applyTemplate,
  getCareTemplateMeta,
  normalizeCareTemplateMetaPlan,
  type CareTemplate,
  type ApplyTemplateResponse,
  type CareTemplateMetaPlan,
} from '@/api/ApplyPremium'
import ToastComponent from '@/components/ToastComponent.vue'
import closeIcon from '@/assets/Fill 1@2x.png'
import downArrowIcon from '@/assets/downarrow@2x.png'
import alertIcon from '@/assets/alert.png'

interface Props {
  visible: boolean
  sourceTemplateId: number | null
  sourcePlanKey: string
}

interface PlanOption {
  label: string
  value: string
}

interface TemplateOption {
  label: string
  value: number
  disabled: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'applied'): void
}>()

const selectedPlan = ref('')
const selectedTemplateId = ref<number | null>(null)
const submitting = ref(false)
const showConfirmModal = ref(false)
const showValidationError = ref(false)
const planDropdownOpen = ref(false)
const templateDropdownOpen = ref(false)
const planSelectRef = ref<HTMLElement | null>(null)
const templateSelectRef = ref<HTMLElement | null>(null)

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

const availableTemplates = ref<CareTemplate[]>([])
const loadingTemplates = ref(false)
const currentTemplate = ref<CareTemplate | null>(null)

// 从后端获取的计划选项
const metaPlans = ref<CareTemplateMetaPlan[]>([])

const planOptions = computed<PlanOption[]>(() => {
  const options = metaPlans.value
    .map((plan) => normalizeCareTemplateMetaPlan(plan))
    .filter((opt) => opt.value)
  if (!props.sourcePlanKey) return options
  // 过滤掉当前源Plan，不能应用到自己
  return options.filter((opt) => opt.value !== props.sourcePlanKey)
})

const templateOptions = computed<TemplateOption[]>(() => {
  if (!selectedPlan.value) return []

  return availableTemplates.value
    .filter((t) => t.id !== props.sourceTemplateId)
    .map((template) => ({
      label: template.template_name,
      value: template.id,
      disabled: false,
    }))
})

const planDisplayValue = computed(() => {
  if (!selectedPlan.value) {
    return 'Select HomeMetric Care Plan'
  }
  const option = planOptions.value.find((opt) => opt.value === selectedPlan.value)
  return option ? option.label : 'Select HomeMetric Care Plan'
})

const templateDisplayValue = computed(() => {
  if (!selectedTemplateId.value) {
    return 'Select template'
  }
  const option = templateOptions.value.find((opt) => opt.value === selectedTemplateId.value)
  return option ? option.label : 'Select template'
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

// 获取计划选项（从meta接口）
async function fetchPlanOptions(): Promise<void> {
  try {
    const response = await getCareTemplateMeta()
    if (response.code === 200 && response.data) {
      metaPlans.value = response.data.plans || []
    }
  } catch {
    // 静默处理错误
  }
}

async function fetchTemplates(): Promise<void> {
  if (!selectedPlan.value) {
    availableTemplates.value = []
    return
  }

  loadingTemplates.value = true

  try {
    const response = await getCareTemplateList({
      plan_key: selectedPlan.value,
    })

    if (response.code === 200 && response.data && response.data.list) {
      availableTemplates.value = response.data.list.filter(
        (t) => t.id > 0 && t.id !== props.sourceTemplateId,
      )
    }
  } catch {
    // 静默处理错误
  } finally {
    loadingTemplates.value = false
  }
}

function selectPlan(value: string): void {
  selectedPlan.value = value
  selectedTemplateId.value = null // 切换Plan时清空模板选择
  planDropdownOpen.value = false
  void fetchTemplates()
}

function selectTemplate(value: number): void {
  selectedTemplateId.value = value
  templateDropdownOpen.value = false
}

// 点击外部关闭下拉框
function handleClickOutside(event: MouseEvent): void {
  if (planSelectRef.value && !planSelectRef.value.contains(event.target as Node)) {
    planDropdownOpen.value = false
  }
  if (templateSelectRef.value && !templateSelectRef.value.contains(event.target as Node)) {
    templateDropdownOpen.value = false
  }
}

function handleApply(): void {
  // 表单验证
  if (!selectedPlan.value || !selectedTemplateId.value) {
    showValidationError.value = true
    showToast('warning', 'Please complete all required fields.')
    return
  }

  // 额外验证：确保选择的是真实存在的模板
  const selectedOption = templateOptions.value.find((opt) => opt.value === selectedTemplateId.value)
  if (!selectedOption || selectedOption.disabled) {
    showToast('error', 'Please select an existing template')
    return
  }

  showConfirmModal.value = true
}

async function confirmApply(): Promise<void> {
  closeConfirm()
  submitting.value = true

  try {
    // ✅ 传递给后端的永远是真实存在的模板ID
    const response: ApplyTemplateResponse = await applyTemplate({
      source_template_id: props.sourceTemplateId!,
      target_template_id: selectedTemplateId.value!,
      confirm_replace: 1,
    })

    if (response.code === 200) {
      emit('applied')
      emit('close') // 立即关闭弹窗
      showToast('success', 'Successfully applied') // 弹窗关闭后显示toast
    } else if (response.code === 409 && response.data?.need_confirm_replace) {
      showToast(
        'warning',
        response.msg || 'Target template already has items. Please confirm replacement.',
      )
      showConfirmModal.value = false
    } else {
      showToast('error', response.msg || 'Failed to apply')
    }
  } catch {
    showToast('error', 'Failed to apply')
  } finally {
    submitting.value = false
  }
}

function closeConfirm(): void {
  showConfirmModal.value = false
}

function handleClose(): void {
  // 重置所有状态
  selectedPlan.value = ''
  selectedTemplateId.value = null
  availableTemplates.value = []
  metaPlans.value = []
  showValidationError.value = false
  planDropdownOpen.value = false
  templateDropdownOpen.value = false
  currentTemplate.value = null
  emit('close')
}


// 监听弹窗显示状态
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      void fetchPlanOptions()
    } else if (!newVal) {
      // 关闭时重置所有状态
      selectedPlan.value = ''
      selectedTemplateId.value = null
      showValidationError.value = false
      planDropdownOpen.value = false
      templateDropdownOpen.value = false

    }
  },
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 16px;
  max-width: 530px;
  width: 90%;
  height: 296px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
@media (max-width: 576px) {
  .modal-container {
    max-width: 100%;
    height: auto;
  }
}

.confirm-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  height: 212px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.modal-header,
.confirm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
}

.modal-title,
.confirm-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  padding: 16px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.confirm-body {
  padding: 24px;
}

.confirm-body {
  font-size: 14px;
  color: #000000;
  line-height: 1.6;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  text-align: left;
  margin-top: -12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #ff4d4f;
  margin-right: 4px;
}

.custom-select {
  position: relative;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px 0 16px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s;
  width: 482px;
  height: 32px;
  box-sizing: border-box;
  position: relative;
}

.select-trigger.has-placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.select-trigger:not(.has-placeholder) {
  color: #000000;
}

.select-trigger:not(.has-placeholder) .select-value {
  color: #000000;
}

.select-trigger.open:not(.disabled),
.select-trigger:focus-within:not(.disabled):not(.error) {
  border-color: #666666;
}

.select-trigger.error {
  border-color: #ff4d4f;
  background: #fffafa;
}

.select-trigger.disabled {
  background-color: #f5f6fb;
  cursor: not-allowed;
  color: #999;
}

.select-value {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-arrow {
  width: 16px;
  height: 16px;
  max-width: 16px;
  max-height: 16px;
  object-fit: contain;
  opacity: 0.55;
  transition: transform 0.2s;
  flex-shrink: 0;
  position: absolute;
  right: 16px;
}

.error-icon {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.select-trigger.open .select-arrow {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  box-sizing: border-box;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  line-height: 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.select-option:hover:not(.disabled) {
  background-color: #f5f5f5;
}

.select-option.disabled {
  color: #ccc;
  cursor: not-allowed;
  background-color: #fafafa;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
  flex: 1;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 16px;
  margin-right: 24px;
  margin-top: 0;
}

.btn-cancel {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #666;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-no {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 16px;
  background-color: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover,
.btn-no:hover {
  background-color: #000000;
  color: #ffffff;
}

.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-confirm {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background-color: #000000;
  color: #ffffff;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

}

.btn-submit:hover:not(:disabled),
.btn-confirm:hover {
  background-color: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
